import { Currency, type Dtos, type ISODate, type Percent } from "@inrev/common";
import { Option } from "@inrev/core";

type BondTerm = Dtos.SuretyBondTerm.Get.Response;

export type BondTermsRange = {
	count: number;
	startYear: number;
	endYear: Option<number>;
};

const yearOf = (date: ISODate): number => Number(date.slice(0, 4));

export const deriveBondTermsRange = (terms: readonly BondTerm[]): Option<BondTermsRange> => {
	if (terms.length === 0) return undefined;

	const expirationYears = Option.filterNone(
		terms.map((term) => Option.map(term.expirationDate, yearOf)),
	);

	return {
		count: terms.length,
		startYear: Math.min(...terms.map((term) => yearOf(term.effectiveDate))),
		endYear: expirationYears.length === 0 ? undefined : Math.max(...expirationYears),
	};
};

export const findCurrentBondTerm = (terms: readonly BondTerm[]): Option<BondTerm> =>
	terms.find((term) => term.status === "current");

export const bondTermOrdinal = (terms: readonly BondTerm[], term: BondTerm): number =>
	terms.findIndex((candidate) => candidate.id === term.id) + 1;

export const deriveTermCommission = (
	totalPremium: Currency,
	commissionPercentage: Percent,
): Currency => Currency.round(totalPremium * commissionPercentage);

type BondWithCommission =
	| { contractSuretyType: "bid"; finalBondAgencyCommissionPercentage: Percent }
	| { contractSuretyType: "final"; agencyCommissionPercentage: Percent };

export const commissionPercentageForBond = (bond: BondWithCommission): Percent =>
	bond.contractSuretyType === "bid"
		? bond.finalBondAgencyCommissionPercentage
		: bond.agencyCommissionPercentage;
